/**
 * FilterBar 状态与业务逻辑 Hook
 */
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import debounce from "lodash/debounce";
import dayjs, { type Dayjs } from "dayjs";
import type { FilterBarProps } from "./FilterBar.types";
import {
  getRegulationsFields,
  getUserFilters,
  createUserFilter,
  updateUserFilter,
  deleteUserFilter,
  reorderUserFilters,
  type FieldConfig,
  type ConceptValue,
  type UserFilter,
  type CreateUserFilterRequest,
} from "../../../services/regulationsService";
import { isFieldConfigColumnFilterable } from "../utils/gridFilterOperatorsFromFieldConfig";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import {
  isFilterFormModified,
  mergeOptionsWithSelected,
  mergeConceptsWithMap,
  fetchConceptMapForCodes,
  fetchConceptsByCodes,
  findNewlyCreatedUserFilter,
  formatCriteriaPart,
  formatDateRangeDisplay,
} from "./filterBarUtils";
import { useConceptAutocomplete } from "./hooks";

export type { FilterBarChecklistItem } from "./filterBarUtils";

const CONCEPT_PAGE_SIZE = 20;
const CONCEPT_SEARCH_DEBOUNCE_MS = 300;
const FILTER_NAME_MAX_LENGTH = 30;

const PRESET_FILTERS: UserFilter[] = [
  {
    id: -7, // Use negative IDs for presets
    name: "Last 7 days",
    criteria: {
      start_date: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
      end_date: dayjs().format("YYYY-MM-DD"),
    },
    is_favorite: false,
    sort_order: -2,
    created_at: "",
    updated_at: "",
  },
  {
    id: -30,
    name: "Last 30 days",
    criteria: {
      start_date: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
      end_date: dayjs().format("YYYY-MM-DD"),
    },
    is_favorite: false,
    sort_order: -1,
    created_at: "",
    updated_at: "",
  },
  {
    id: -1, // Use negative IDs for presets
    name: "Show Favorite",
    criteria: {
      is_favorite: true as any,
    },
    is_favorite: false,
    sort_order: 0,
    created_at: "",
    updated_at: "",
  },
];

/** Preset rows prepended to `savedFilters` (not shown in Manage dialog). */
export const SAVED_FILTER_PRESET_COUNT = PRESET_FILTERS.length;

/** "Show Favorite" preset filter id — list uses GET /regulations/favorites when active */
export const SHOW_FAVORITE_PRESET_FILTER_ID = -1;

export function useFilterBar(props: FilterBarProps) {
  const {
    selectedMarkets,
    onMarketsChange,
    selectedProductTypes,
    onProductTypesChange,
    selectedHA,
    onHAChange,
    selectedTags,
    onTagsChange,
    selectedTitle,
    onTitleChange,
    fromDate,
    toDate,
    onDateRangeChange,
    onFavoriteFilterChange,
    initialActiveSavedFilterId,
  } = props;

  const [filterableFields, setFilterableFields] = useState<FieldConfig[]>([]);
  const [searchInput, setSearchInput] = useState(selectedTitle || "");

  const marketsAc = useConceptAutocomplete("Country", CONCEPT_PAGE_SIZE);
  const productTypesAc = useConceptAutocomplete("ProductType", CONCEPT_PAGE_SIZE);
  const tagsAc = useConceptAutocomplete("Tags", CONCEPT_PAGE_SIZE);
  const haAc = useConceptAutocomplete("HA", CONCEPT_PAGE_SIZE);

  const { showMessage } = useSnackbar();

  const [savedFilters, setSavedFilters] = useState<UserFilter[]>([]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const [activeFilterId, setActiveFilterId] = useState<number | null>(() =>
    initialActiveSavedFilterId ?? null,
  );
  const [isFormModified, setIsFormModified] = useState(false);
  const [hasFormBeenInteracted, setHasFormBeenInteracted] = useState(false);
  const [activeFilterOriginalData, setActiveFilterOriginalData] = useState<{
    markets: ConceptValue[];
    productTypes: ConceptValue[];
    ha: ConceptValue[];
    tags: ConceptValue[];
    searchTerm: string;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
  } | null>(null);

  const [marketsMap, setMarketsMap] = useState<Map<string, string>>(new Map());
  const [productTypesMap, setProductTypesMap] = useState<Map<string, string>>(
    new Map(),
  );
  const [haMap, setHaMap] = useState<Map<string, string>>(new Map());
  const [tagsMap, setTagsMap] = useState<Map<string, string>>(new Map());

  const [manageFiltersOpen, setManageFiltersOpen] = useState(false);
  const [isCreatingFilter, setIsCreatingFilter] = useState(false);
  // Use independent input state for the dialog form to avoid clearing the
  // homepage FilterBarMainRow when the dialog opens.
  const [dialogProductTypesInput, setDialogProductTypesInput] = useState("");
  const [dialogMarketsInput, setDialogMarketsInput] = useState("");
  const [dialogHaInput, setDialogHaInput] = useState("");
  const [dialogTagsInput, setDialogTagsInput] = useState("");
  const [isSavingFilter, setIsSavingFilter] = useState(false);
  const [editingFilter, setEditingFilter] = useState<UserFilter | null>(null);
  const [isLoadingEditData, setIsLoadingEditData] = useState(false);

  const [newFilterName, setNewFilterName] = useState("");
  const [filterNameError, setFilterNameError] = useState("");
  const [newFilterProductTypes, setNewFilterProductTypes] = useState<
    ConceptValue[]
  >([]);
  const [newFilterMarkets, setNewFilterMarkets] = useState<ConceptValue[]>([]);
  const [newFilterHA, setNewFilterHA] = useState<ConceptValue[]>([]);
  const [newFilterTags, setNewFilterTags] = useState<ConceptValue[]>([]);
  const [newFilterSearchTerm, setNewFilterSearchTerm] = useState("");
  const [newFilterFromDate, setNewFilterFromDate] = useState<Dayjs | null>(
    null,
  );
  const [newFilterToDate, setNewFilterToDate] = useState<Dayjs | null>(null);
  const [newFilterIsFavorite, setNewFilterIsFavorite] = useState(false);

  const [dateRangeAnchorEl, setDateRangeAnchorEl] =
    useState<HTMLElement | null>(null);

  const productTypesOptionsWithSelected = useMemo(
    () =>
      mergeOptionsWithSelected(
        productTypesAc.options,
        newFilterProductTypes,
        dialogProductTypesInput,
      ),
    [newFilterProductTypes, productTypesAc.options, dialogProductTypesInput],
  );

  const marketsOptionsWithSelected = useMemo(
    () =>
      mergeOptionsWithSelected(
        marketsAc.options,
        newFilterMarkets,
        dialogMarketsInput,
      ),
    [newFilterMarkets, marketsAc.options, dialogMarketsInput],
  );

  const tagsOptionsWithSelected = useMemo(
    () =>
      mergeOptionsWithSelected(tagsAc.options, newFilterTags, dialogTagsInput),
    [newFilterTags, tagsAc.options, dialogTagsInput],
  );

  const haOptionsWithSelected = useMemo(
    () => mergeOptionsWithSelected(haAc.options, newFilterHA, dialogHaInput),
    [newFilterHA, haAc.options, dialogHaInput],
  );

  const mainRowMarketsOptionsWithSelected = useMemo(
    () =>
      mergeOptionsWithSelected(
        marketsAc.options,
        selectedMarkets,
        marketsAc.input,
      ),
    [selectedMarkets, marketsAc.options, marketsAc.input],
  );

  const mainRowProductTypesOptionsWithSelected = useMemo(
    () =>
      mergeOptionsWithSelected(
        productTypesAc.options,
        selectedProductTypes,
        productTypesAc.input,
      ),
    [selectedProductTypes, productTypesAc.options, productTypesAc.input],
  );

  const mainRowHaOptionsWithSelected = useMemo(
    () =>
      mergeOptionsWithSelected(haAc.options, selectedHA, haAc.input),
    [selectedHA, haAc.options, haAc.input],
  );

  const mainRowTagsOptionsWithSelected = useMemo(
    () =>
      mergeOptionsWithSelected(tagsAc.options, selectedTags, tagsAc.input),
    [selectedTags, tagsAc.options, tagsAc.input],
  );

  const handleOpenDateRange = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setDateRangeAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleCloseDateRange = useCallback(() => {
    setDateRangeAnchorEl(null);
  }, []);

  const handleDateRangeChange = useCallback(
    (from: Dayjs | null, to: Dayjs | null) => {
      setHasFormBeenInteracted(true);
      onDateRangeChange(from, to);
    },
    [onDateRangeChange],
  );

  const dateRangeOpen = Boolean(dateRangeAnchorEl);

  const dateRangeDisplay = useMemo(
    () => formatDateRangeDisplay(fromDate, toDate),
    [fromDate, toDate],
  );

  useEffect(() => {
    const loadFields = async () => {
      try {
        const response = await getRegulationsFields("list");
        if (response.code === 200 && response.data) {
          const listFilterableFields = response.data.fields.filter(
            (field: FieldConfig) =>
              field.pages.includes("list") && isFieldConfigColumnFilterable(field),
          );
          setFilterableFields(listFilterableFields);
        }
      } catch (error) {
        console.error("❌ Failed to load fields configuration:", error);
      }
    };
    loadFields();
  }, []);

  const loadSavedFilters = useCallback(async () => {
    try {
      setSavedFiltersLoading(true);
      const response = await getUserFilters();
      if (response.code === 200 && response.data) {
        // Sort so favorite filters appear first on the landing page
        const sorted = [...response.data].sort(
          (a, b) => (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0),
        );
        setSavedFilters([...PRESET_FILTERS, ...sorted]);
        const marketCodes = new Set<string>();
        const productTypeCodes = new Set<string>();
        const haCodes = new Set<string>();
        const tagCodes = new Set<string>();
        response.data.forEach((filter) => {
          filter.criteria.markets?.forEach((code) => marketCodes.add(code));
          filter.criteria.product_types?.forEach((code) =>
            productTypeCodes.add(code),
          );
          if (Array.isArray(filter.criteria.ha)) {
            filter.criteria.ha.forEach((code) => haCodes.add(code));
          }
          if (Array.isArray(filter.criteria.tags)) {
            filter.criteria.tags.forEach((code) => tagCodes.add(code));
          }
        });
        const [marketsMapResult, productTypesMapResult, haMapResult, tagsMapResult] =
          await Promise.all([
            fetchConceptMapForCodes("Country", marketCodes),
            fetchConceptMapForCodes("ProductType", productTypeCodes),
            fetchConceptMapForCodes("HA", haCodes),
            fetchConceptMapForCodes("Tags", tagCodes),
          ]);
        if (marketsMapResult.size > 0) setMarketsMap(marketsMapResult);
        if (productTypesMapResult.size > 0)
          setProductTypesMap(productTypesMapResult);
        if (haMapResult.size > 0) setHaMap(haMapResult);
        if (tagsMapResult.size > 0) setTagsMap(tagsMapResult);
        return response.data;
      }
      setSavedFilters(PRESET_FILTERS);
      return PRESET_FILTERS;
    } catch (error) {
      console.error("❌ Failed to load saved filters:", error);
      setSavedFilters(PRESET_FILTERS);
      return PRESET_FILTERS;
    } finally {
      setSavedFiltersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSavedFilters();
  }, [loadSavedFilters]);

  const debouncedLoadMarketsOptions = useMemo(
    () => debounce((search?: string) => marketsAc.loadOptions(search), CONCEPT_SEARCH_DEBOUNCE_MS),
    [marketsAc.loadOptions],
  );
  useEffect(() => () => debouncedLoadMarketsOptions.cancel(), [debouncedLoadMarketsOptions]);

  const handleMarketsInputChange = useCallback(
    (_event: unknown, value: string) => {
      marketsAc.setInput(value);
      if (value.length === 0) {
        debouncedLoadMarketsOptions.cancel();
        marketsAc.loadOptions(undefined);
      } else {
        debouncedLoadMarketsOptions(value);
      }
    },
    [marketsAc, debouncedLoadMarketsOptions],
  );

  const handleMarketsChange = useCallback(
    (_event: unknown, value: ConceptValue[]) => {
      setHasFormBeenInteracted(true);
      onMarketsChange(value ?? []);
    },
    [onMarketsChange],
  );

  const debouncedLoadProductTypesOptions = useMemo(
    () => debounce((search?: string) => productTypesAc.loadOptions(search), CONCEPT_SEARCH_DEBOUNCE_MS),
    [productTypesAc.loadOptions],
  );
  const debouncedLoadHAOptions = useMemo(
    () => debounce((search?: string) => haAc.loadOptions(search), CONCEPT_SEARCH_DEBOUNCE_MS),
    [haAc.loadOptions],
  );
  const debouncedLoadTagsOptions = useMemo(
    () => debounce((search?: string) => tagsAc.loadOptions(search), CONCEPT_SEARCH_DEBOUNCE_MS),
    [tagsAc.loadOptions],
  );
  useEffect(() => () => {
    debouncedLoadProductTypesOptions.cancel();
    debouncedLoadHAOptions.cancel();
    debouncedLoadTagsOptions.cancel();
  }, [debouncedLoadProductTypesOptions, debouncedLoadHAOptions, debouncedLoadTagsOptions]);

  const handleProductTypesInputChange = useCallback(
    (_event: unknown, value: string) => {
      productTypesAc.setInput(value);
      if (value.length === 0) {
        debouncedLoadProductTypesOptions.cancel();
        productTypesAc.loadOptions(undefined);
      } else {
        debouncedLoadProductTypesOptions(value);
      }
    },
    [productTypesAc, debouncedLoadProductTypesOptions],
  );
  const handleHAInputChange = useCallback(
    (_event: unknown, value: string) => {
      haAc.setInput(value);
      if (value.length === 0) {
        debouncedLoadHAOptions.cancel();
        haAc.loadOptions(undefined);
      } else {
        debouncedLoadHAOptions(value);
      }
    },
    [haAc, debouncedLoadHAOptions],
  );
  const handleTagsInputChange = useCallback(
    (_event: unknown, value: string) => {
      tagsAc.setInput(value);
      if (value.length === 0) {
        debouncedLoadTagsOptions.cancel();
        tagsAc.loadOptions(undefined);
      } else {
        debouncedLoadTagsOptions(value);
      }
    },
    [tagsAc, debouncedLoadTagsOptions],
  );

  const loadMarketsOptionsForDialog = useCallback(
    (search?: string) => {
      if (!search || search.length === 0) {
        debouncedLoadMarketsOptions.cancel();
        marketsAc.loadOptions(undefined);
      } else {
        debouncedLoadMarketsOptions(search);
      }
    },
    [marketsAc, debouncedLoadMarketsOptions],
  );
  const loadProductTypesOptionsForDialog = useCallback(
    (search?: string) => {
      if (!search || search.length === 0) {
        debouncedLoadProductTypesOptions.cancel();
        productTypesAc.loadOptions(undefined);
      } else {
        debouncedLoadProductTypesOptions(search);
      }
    },
    [productTypesAc, debouncedLoadProductTypesOptions],
  );
  const loadHAOptionsForDialog = useCallback(
    (search?: string) => {
      if (!search || search.length === 0) {
        debouncedLoadHAOptions.cancel();
        haAc.loadOptions(undefined);
      } else {
        debouncedLoadHAOptions(search);
      }
    },
    [haAc, debouncedLoadHAOptions],
  );
  const loadTagsOptionsForDialog = useCallback(
    (search?: string) => {
      if (!search || search.length === 0) {
        debouncedLoadTagsOptions.cancel();
        tagsAc.loadOptions(undefined);
      } else {
        debouncedLoadTagsOptions(search);
      }
    },
    [tagsAc, debouncedLoadTagsOptions],
  );

  useEffect(() => {
    setSearchInput(selectedTitle || "");
  }, [selectedTitle]);

  const handleSearchKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        onTitleChange(searchInput);
      }
    },
    [searchInput, onTitleChange],
  );

  const handleSearchSubmit = useCallback(
    (value?: string) => {
      const searchValue = typeof value === 'string' ? value : searchInput;
      onTitleChange(searchValue);
    },
    [searchInput, onTitleChange],
  );

  const handleSearchBlur = useCallback(() => {
    if (searchInput !== selectedTitle) {
      onTitleChange(searchInput);
    }
  }, [searchInput, selectedTitle, onTitleChange]);

  /** Baseline for isFormModified — must match saved filter criteria, not the live store snapshot. */
  const hasHydratedActiveFilterBaselineRef = useRef(false);

  const loadFilterFormOriginalFromUserFilter = useCallback(
    async (filter: UserFilter) => {
      const [markets, productTypes, ha, tags] = await Promise.all([
        fetchConceptsByCodes("Country", filter.criteria.markets ?? []),
        fetchConceptsByCodes(
          "ProductType",
          filter.criteria.product_types ?? [],
        ),
        fetchConceptsByCodes("HA", filter.criteria.ha ?? []),
        fetchConceptsByCodes("Tags", filter.criteria.tags ?? []),
      ]);
      const appliedMarkets = mergeConceptsWithMap(
        filter.criteria.markets ?? [],
        markets,
        marketsMap,
      );
      const appliedProductTypes = mergeConceptsWithMap(
        filter.criteria.product_types ?? [],
        productTypes,
        productTypesMap,
      );
      const appliedHA = mergeConceptsWithMap(
        filter.criteria.ha ?? [],
        ha,
        haMap,
      );
      const appliedTags = mergeConceptsWithMap(
        filter.criteria.tags ?? [],
        tags,
        tagsMap,
      );
      const appliedSearchTerm = filter.criteria.keyword ?? "";
      const appliedFromDate = filter.criteria.start_date
        ? dayjs(filter.criteria.start_date)
        : null;
      const appliedToDate = filter.criteria.end_date
        ? dayjs(filter.criteria.end_date)
        : null;

      return {
        markets: appliedMarkets,
        productTypes: appliedProductTypes,
        ha: appliedHA,
        tags: appliedTags,
        searchTerm: appliedSearchTerm,
        fromDate: appliedFromDate,
        toDate: appliedToDate,
      };
    },
    [marketsMap, productTypesMap, haMap, tagsMap],
  );

  useEffect(() => {
    hasHydratedActiveFilterBaselineRef.current = false;
  }, [activeFilterId]);

  /**
   * activeFilterId is restored from the store on mount, but activeFilterOriginalData is not.
   * Build baseline from the saved filter's criteria (not selectedMarkets/selectedTags in store).
   */
  useEffect(() => {
    if (activeFilterId == null || activeFilterId < 0) {
      return;
    }
    if (
      savedFiltersLoading ||
      hasHydratedActiveFilterBaselineRef.current ||
      activeFilterOriginalData != null
    ) {
      return;
    }
    const filter = savedFilters.find((f) => f.id === activeFilterId);
    if (!filter) return;

    hasHydratedActiveFilterBaselineRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const original = await loadFilterFormOriginalFromUserFilter(filter);
        if (!cancelled) {
          setActiveFilterOriginalData(original);
        }
      } catch (error) {
        console.error("Failed to hydrate active filter baseline:", error);
        if (!cancelled) {
          hasHydratedActiveFilterBaselineRef.current = false;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeFilterId,
    activeFilterOriginalData,
    savedFiltersLoading,
    savedFilters,
    loadFilterFormOriginalFromUserFilter,
  ]);

  useEffect(() => {
    if (!activeFilterOriginalData) {
      setIsFormModified(false);
      return;
    }
    setIsFormModified(
      isFilterFormModified(
        {
          markets: selectedMarkets,
          productTypes: selectedProductTypes,
          ha: selectedHA,
          tags: selectedTags,
          searchTerm: selectedTitle,
          fromDate,
          toDate,
        },
        activeFilterOriginalData,
      ),
    );
  }, [
    selectedMarkets,
    selectedProductTypes,
    selectedHA,
    selectedTags,
    selectedTitle,
    fromDate,
    toDate,
    activeFilterOriginalData,
  ]);

  const handleApplySavedFilter = useCallback(
    async (filter: UserFilter) => {
      try {
        hasHydratedActiveFilterBaselineRef.current = true;
        setActiveFilterId(filter.id);
        const original = await loadFilterFormOriginalFromUserFilter(filter);

        onMarketsChange(original.markets);
        onProductTypesChange(original.productTypes);
        onHAChange(original.ha);
        onTagsChange(original.tags);

        if (original.searchTerm) {
          setSearchInput(original.searchTerm);
          onTitleChange(original.searchTerm);
        } else {
          setSearchInput("");
          onTitleChange("");
        }

        onDateRangeChange(original.fromDate, original.toDate);
        setActiveFilterOriginalData(original);
      } catch (error) {
        console.error("❌ Error applying saved filter:", error);
        hasHydratedActiveFilterBaselineRef.current = false;
      }
    },
    [
      loadFilterFormOriginalFromUserFilter,
      onMarketsChange,
      onProductTypesChange,
      onHAChange,
      onTagsChange,
      onTitleChange,
      onDateRangeChange,
    ],
  );

  // Auto-apply favorite user filter on landing when none is selected
  const hasAutoAppliedFavoriteRef = useRef(false);
  useEffect(() => {
    if (
      savedFiltersLoading ||
      savedFilters.length === 0 ||
      activeFilterId !== null ||
      hasAutoAppliedFavoriteRef.current
    )
      return;
    const favorite = savedFilters.find((f) => f.is_favorite);
    if (!favorite) {
      hasAutoAppliedFavoriteRef.current = true;
      return;
    }
    hasAutoAppliedFavoriteRef.current = true;
    void handleApplySavedFilter(favorite);
  }, [
    savedFiltersLoading,
    savedFilters,
    activeFilterId,
    handleApplySavedFilter,
  ]);

  const handleRefreshFilter = useCallback(async () => {
    if (activeFilterId == null || activeFilterId < 0) return;
    const activeFilter = savedFilters.find((f) => f.id === activeFilterId);
    if (!activeFilter) return;

    const request: CreateUserFilterRequest = {
      name: activeFilter.name.trim(),
      criteria: {
        markets: selectedMarkets.map((m) => m.code),
        product_types: selectedProductTypes.map((pt) => pt.code),
        ha: selectedHA.map((h) => h.code),
        tags: selectedTags.map((t) => t.code),
        start_date: fromDate?.format("YYYY-MM-DD"),
        end_date: toDate?.format("YYYY-MM-DD"),
        keyword: selectedTitle.trim() || undefined,
      },
      is_favorite: activeFilter.is_favorite,
    };

    setIsSavingFilter(true);
    try {
      const response = await updateUserFilter(activeFilter.id, request);
      const isSuccess = response.code === 200 || response.code === 201;
      if (!isSuccess) {
        alert(
          `Failed to update filter: ${response.message ?? "Unknown error"}`,
        );
        return;
      }

      const savedMarkets = [...selectedMarkets];
      const savedProductTypes = [...selectedProductTypes];
      const savedHA = [...selectedHA];
      const savedTags = [...selectedTags];

      const latestFilters = await loadSavedFilters();
      const latestFilter = latestFilters.find((f) => f.id === activeFilter.id);
      if (latestFilter) {
        await handleApplySavedFilter(latestFilter);
      }

      setMarketsMap((prev) => {
        const next = new Map(prev);
        savedMarkets.forEach((m) => next.set(m.code, m.name ?? m.code));
        return next;
      });
      setProductTypesMap((prev) => {
        const next = new Map(prev);
        savedProductTypes.forEach((pt) =>
          next.set(pt.code, pt.name ?? pt.code),
        );
        return next;
      });
      setHaMap((prev) => {
        const next = new Map(prev);
        savedHA.forEach((h) => next.set(h.code, h.name ?? h.code));
        return next;
      });
      setTagsMap((prev) => {
        const next = new Map(prev);
        savedTags.forEach((t) => next.set(t.code, t.name ?? t.code));
        return next;
      });

      showMessage("Filter updated successfully", "success");
    } catch (error) {
      console.error("Error updating filter:", error);
      alert(
        `Error updating filter: ${
          (error as Error)?.message ?? "Unknown error"
        }`,
      );
    } finally {
      setIsSavingFilter(false);
    }
  }, [
    activeFilterId,
    savedFilters,
    selectedMarkets,
    selectedProductTypes,
    selectedHA,
    selectedTags,
    selectedTitle,
    fromDate,
    toDate,
    loadSavedFilters,
    handleApplySavedFilter,
    showMessage,
  ]);

  const handleSaveAsNewFilter = useCallback(() => {
    setFilterNameError("");
    if (activeFilterId) {
      const currentFilter = savedFilters.find((f) => f.id === activeFilterId);
      if (currentFilter) {
        setNewFilterName(
          `${currentFilter.name.slice(0, FILTER_NAME_MAX_LENGTH - 5)} Copy`,
        );
      }
    } else {
      setNewFilterName("");
    }
    setNewFilterMarkets([...selectedMarkets]);
    setNewFilterProductTypes([...selectedProductTypes]);
    setNewFilterHA([...selectedHA]);
    setNewFilterTags([...selectedTags]);
    setNewFilterSearchTerm(selectedTitle);
    setNewFilterFromDate(fromDate);
    setNewFilterToDate(toDate);
    setNewFilterIsFavorite(false);
    setManageFiltersOpen(true);
    setIsCreatingFilter(true);
    setEditingFilter(null);
  }, [
    activeFilterId,
    savedFilters,
    selectedMarkets,
    selectedProductTypes,
    selectedHA,
    selectedTags,
    selectedTitle,
    fromDate,
    toDate,
  ]);

  const dismissShowFavoritePreset = useCallback(() => {
    setActiveFilterId((current) =>
      current === SHOW_FAVORITE_PRESET_FILTER_ID ? null : current,
    );
  }, []);

  const handleClearFilters = useCallback(() => {
    hasHydratedActiveFilterBaselineRef.current = false;
    setActiveFilterId(null);
    setActiveFilterOriginalData(null);
    setIsFormModified(false);
    setHasFormBeenInteracted(false);
    onMarketsChange([]);
    marketsAc.setInput("");
    onProductTypesChange([]);
    productTypesAc.setInput("");
    onHAChange([]);
    haAc.setInput("");
    onTagsChange([]);
    tagsAc.setInput("");
    setSearchInput("");
    onTitleChange("");
    onDateRangeChange(null, null);
    onFavoriteFilterChange?.(null);
  }, [
    onMarketsChange,
    onProductTypesChange,
    onHAChange,
    onTagsChange,
    onTitleChange,
    onDateRangeChange,
    onFavoriteFilterChange,
    marketsAc,
    productTypesAc,
    haAc,
    tagsAc,
  ]);

  const formatFilterCriteria = useCallback(
    (filter: UserFilter): string => {
      const parts = [
        formatCriteriaPart("Markets", filter.criteria.markets, marketsMap),
        formatCriteriaPart(
          "Product Types",
          filter.criteria.product_types,
          productTypesMap,
        ),
        formatCriteriaPart("HA", filter.criteria.ha, haMap),
        formatCriteriaPart("Tags", filter.criteria.tags, tagsMap),
      ].filter((p): p is string => p != null);
      if (filter.criteria.keyword) {
        parts.push(`Keyword: ${filter.criteria.keyword}`);
      }
      if (filter.criteria.start_date || filter.criteria.end_date) {
        const formatCriteriaDate = (dateStr?: string) => {
          if (!dateStr) return null;
          const d = dayjs(dateStr);
          return d.isValid() ? d.format("DD-MMM-YYYY") : String(dateStr);
        };

        const dateRange = [
          formatCriteriaDate(filter.criteria.start_date),
          formatCriteriaDate(filter.criteria.end_date),
        ]
          .filter(Boolean)
          .join(" ~ ");
        parts.push(`Date: ${dateRange}`);
      }
      return parts.length > 0 ? parts.join(", ") : "No criteria set";
    },
    [marketsMap, productTypesMap, haMap, tagsMap],
  );

  const resetDialogFilterForm = useCallback(() => {
    setNewFilterName("");
    setFilterNameError("");
    setNewFilterProductTypes([]);
    setNewFilterMarkets([]);
    setNewFilterHA([]);
    setNewFilterTags([]);
    setNewFilterSearchTerm("");
    setNewFilterFromDate(null);
    setNewFilterToDate(null);
    setNewFilterIsFavorite(false);
    setEditingFilter(null);
    setIsLoadingEditData(false);
    setDialogProductTypesInput("");
    setDialogMarketsInput("");
    setDialogHaInput("");
    setDialogTagsInput("");
  }, []);

  const handleMoveSavedFilter = useCallback(
    async (fromIndex: number, toIndex: number) => {
      if (toIndex < 0 || toIndex >= savedFilters.length) return;
      const previousFilters = savedFilters;
      const nextFilters = [...savedFilters];
      const [moved] = nextFilters.splice(fromIndex, 1);
      nextFilters.splice(toIndex, 0, moved);
      setSavedFilters(nextFilters);
      try {
        const userIds = nextFilters
          .map((filter) => filter.id)
          .filter((id) => id > 0);
        await reorderUserFilters(userIds);
      } catch (error) {
        console.error("❌ Failed to reorder filters:", error);
        setSavedFilters(previousFilters);
      }
    },
    [savedFilters],
  );

  const handleToggleFavoriteFilter = useCallback(
    async (targetFilter: UserFilter) => {
      const previousFilters = savedFilters;
      const previousFavorite = savedFilters.find(
        (filter) => filter.is_favorite && filter.id !== targetFilter.id,
      );
      const shouldUnsetFavorite = targetFilter.is_favorite;
      const nextFilters = savedFilters.map((filter) => ({
        ...filter,
        is_favorite: shouldUnsetFavorite
          ? false
          : filter.id === targetFilter.id,
      }));
      setSavedFilters(nextFilters);
      try {
        const updateRequests = [
          updateUserFilter(targetFilter.id, {
            name: targetFilter.name,
            criteria: targetFilter.criteria,
            is_favorite: !shouldUnsetFavorite,
          }),
        ];
        if (!shouldUnsetFavorite && previousFavorite) {
          updateRequests.push(
            updateUserFilter(previousFavorite.id, {
              name: previousFavorite.name,
              criteria: previousFavorite.criteria,
              is_favorite: false,
            }),
          );
        }
        await Promise.all(updateRequests);
      } catch (error) {
        console.error("❌ Failed to update favorite filter:", error);
        setSavedFilters(previousFilters);
      }
    },
    [savedFilters],
  );

  const setNewFilterNameAndClearError = useCallback((v: string) => {
    setNewFilterName(v);
    setFilterNameError("");
  }, []);

  const handleEditFilter = useCallback(
    async (filter: UserFilter) => {
      setEditingFilter(filter);
      setIsCreatingFilter(true);
      setIsLoadingEditData(true);
      setFilterNameError("");
      setNewFilterName(filter.name.slice(0, FILTER_NAME_MAX_LENGTH));
      setNewFilterSearchTerm(filter.criteria.keyword ?? "");
      setNewFilterFromDate(
        filter.criteria.start_date ? dayjs(filter.criteria.start_date) : null,
      );
      setNewFilterToDate(
        filter.criteria.end_date ? dayjs(filter.criteria.end_date) : null,
      );
      setNewFilterIsFavorite(filter.is_favorite);
      setNewFilterMarkets([]);
      setNewFilterProductTypes([]);
      setNewFilterHA([]);
      setNewFilterTags([]);
      try {
        const [markets, productTypes, ha, tags] = await Promise.all([
          fetchConceptsByCodes("Country", filter.criteria.markets ?? []),
          fetchConceptsByCodes(
            "ProductType",
            filter.criteria.product_types ?? [],
          ),
          fetchConceptsByCodes("HA", filter.criteria.ha ?? []),
          fetchConceptsByCodes("Tags", filter.criteria.tags ?? []),
        ]);
        // fetchConceptsByCodes only gets first page; items selected via search
        // may be missing. Merge with maps (from loadSavedFilters or save merge).
        setNewFilterMarkets(
          mergeConceptsWithMap(
            filter.criteria.markets ?? [],
            markets,
            marketsMap,
          ),
        );
        setNewFilterProductTypes(
          mergeConceptsWithMap(
            filter.criteria.product_types ?? [],
            productTypes,
            productTypesMap,
          ),
        );
        setNewFilterHA(
          mergeConceptsWithMap(filter.criteria.ha ?? [], ha, haMap),
        );
        setNewFilterTags(
          mergeConceptsWithMap(filter.criteria.tags ?? [], tags, tagsMap),
        );
      } finally {
        setIsLoadingEditData(false);
      }
    },
    [marketsMap, productTypesMap, haMap, tagsMap],
  );

  const buildFilterRequest = useCallback((): CreateUserFilterRequest => ({
    name: newFilterName.trim(),
    criteria: {
      markets: newFilterMarkets.map((m) => m.code),
      product_types: newFilterProductTypes.map((pt) => pt.code),
      ha: newFilterHA.map((h) => h.code),
      tags: newFilterTags.map((t) => t.code),
      start_date: newFilterFromDate?.format("YYYY-MM-DD"),
      end_date: newFilterToDate?.format("YYYY-MM-DD"),
      keyword: newFilterSearchTerm.trim() || undefined,
    },
    is_favorite: newFilterIsFavorite,
  }), [
    newFilterName,
    newFilterMarkets,
    newFilterProductTypes,
    newFilterHA,
    newFilterTags,
    newFilterFromDate,
    newFilterToDate,
    newFilterSearchTerm,
    newFilterIsFavorite,
  ]);

  const handleSaveFilter = useCallback(async () => {
    if (!newFilterName.trim()) {
      setFilterNameError("Fill in required fields");
      return;
    }
    setFilterNameError("");
    setIsSavingFilter(true);
    const previousUserFilterIds = new Set(
      savedFilters.filter((f) => f.id >= 0).map((f) => f.id),
    );
    const createdFilterName = newFilterName.trim();
    try {
      const request = buildFilterRequest();
      const response = editingFilter
        ? await updateUserFilter(editingFilter.id, request)
        : await createUserFilter(request);

      const isSuccess = response.code === 200 || response.code === 201;
      if (!isSuccess) {
        alert(
          `Failed to ${editingFilter ? "update" : "save"} filter: ${
            response.message ?? "Unknown error"
          }`,
        );
        return;
      }

      // Capture ConceptValues before reset so we can merge names into maps.
      // When user selects via dropdown search, the item may not be on the first
      // page of getConceptValues, so fetchConceptMapForCodes won't find it.
      // Merging these names ensures the saved filter displays labels, not codes.
      const savedMarkets = [...newFilterMarkets];
      const savedProductTypes = [...newFilterProductTypes];
      const savedHA = [...newFilterHA];
      const savedTags = [...newFilterTags];

      const latestUserFilters = (await loadSavedFilters()) ?? [];
      const responseFilters = Array.isArray(response.data) ? response.data : [];

      if (editingFilter) {
        if (activeFilterId === editingFilter.id) {
          const latestFilter = latestUserFilters.find(
            (f) => f.id === editingFilter.id,
          );
          if (latestFilter) await handleApplySavedFilter(latestFilter);
        }
      } else {
        const createdFilter =
          findNewlyCreatedUserFilter(
            responseFilters,
            previousUserFilterIds,
            createdFilterName,
          ) ??
          findNewlyCreatedUserFilter(
            latestUserFilters,
            previousUserFilterIds,
            createdFilterName,
          );
        if (createdFilter) {
          await handleApplySavedFilter(createdFilter);
        }
      }

      // Merge names from the form into maps so display shows labels, not codes
      setMarketsMap((prev) => {
        const next = new Map(prev);
        savedMarkets.forEach((m) => next.set(m.code, m.name ?? m.code));
        return next;
      });
      setProductTypesMap((prev) => {
        const next = new Map(prev);
        savedProductTypes.forEach((pt) => next.set(pt.code, pt.name ?? pt.code));
        return next;
      });
      setHaMap((prev) => {
        const next = new Map(prev);
        savedHA.forEach((h) => next.set(h.code, h.name ?? h.code));
        return next;
      });
      setTagsMap((prev) => {
        const next = new Map(prev);
        savedTags.forEach((t) => next.set(t.code, t.name ?? t.code));
        return next;
      });

      resetDialogFilterForm();
      setIsCreatingFilter(false);
    } catch (error) {
      console.error(
        `❌ Error ${editingFilter ? "updating" : "saving"} filter:`,
        error,
      );
      alert(
        `Error ${editingFilter ? "updating" : "saving"} filter: ${
          (error as Error)?.message ?? "Unknown error"
        }`,
      );
    } finally {
      setIsSavingFilter(false);
    }
  }, [
    newFilterName,
    buildFilterRequest,
    editingFilter,
    resetDialogFilterForm,
    loadSavedFilters,
    activeFilterId,
    handleApplySavedFilter,
    savedFilters,
  ]);

  return {
    filterableFields,
    selectedMarkets,
    marketsInput: marketsAc.input,
    setMarketsInput: marketsAc.setInput,
    marketsOptions: marketsAc.options,
    marketsLoading: marketsAc.loading,
    mainRowMarketsOptionsWithSelected,
    productTypesInput: productTypesAc.input,
    setProductTypesInput: productTypesAc.setInput,
    productTypesOptions: productTypesAc.options,
    productTypesLoading: productTypesAc.loading,
    selectedProductTypes,
    onProductTypesChange,
    mainRowProductTypesOptionsWithSelected,
    tagsInput: tagsAc.input,
    setTagsInput: tagsAc.setInput,
    tagsOptions: tagsAc.options,
    tagsLoading: tagsAc.loading,
    selectedTags,
    onTagsChange,
    mainRowTagsOptionsWithSelected,
    haInput: haAc.input,
    setHaInput: haAc.setInput,
    haOptions: haAc.options,
    haLoading: haAc.loading,
    selectedHA,
    onHAChange,
    mainRowHaOptionsWithSelected,
    searchInput,
    setSearchInput,
    savedFilters,
    setSavedFilters,
    savedFiltersLoading,
    activeFilterId,
    setActiveFilterId,
    isFormModified,
    hasFormBeenInteracted,
    isSavingFilter,
    activeFilterOriginalData,
    manageFiltersOpen,
    setManageFiltersOpen,
    isCreatingFilter,
    setIsCreatingFilter,
    editingFilter,
    isLoadingEditData,
    newFilterName,
    setNewFilterName: setNewFilterNameAndClearError,
    filterNameError,
    newFilterProductTypes,
    setNewFilterProductTypes,
    newFilterMarkets,
    setNewFilterMarkets,
    newFilterHA,
    setNewFilterHA,
    newFilterTags,
    setNewFilterTags,
    newFilterSearchTerm,
    setNewFilterSearchTerm,
    newFilterFromDate,
    setNewFilterFromDate,
    newFilterToDate,
    setNewFilterToDate,
    productTypesOptionsWithSelected,
    marketsOptionsWithSelected,
    tagsOptionsWithSelected,
    haOptionsWithSelected,
    dialogProductTypesInput,
    setDialogProductTypesInput,
    dialogMarketsInput,
    setDialogMarketsInput,
    dialogHaInput,
    setDialogHaInput,
    dialogTagsInput,
    setDialogTagsInput,
    dateRangeAnchorEl,
    handleOpenDateRange,
    handleCloseDateRange,
    handleDateRangeChange,
    dateRangeOpen,
    dateRangeDisplay,
    fromDate,
    toDate,
    loadSavedFilters,
    formatFilterCriteria,
    resetDialogFilterForm,
    handleApplySavedFilter,
    handleRefreshFilter,
    handleSaveAsNewFilter,
    handleClearFilters,
    dismissShowFavoritePreset,
    handleMoveSavedFilter,
    handleToggleFavoriteFilter,
    handleEditFilter,
    handleSaveFilter,
    loadMarketsOptions: marketsAc.loadOptions,
    loadMarketsOptionsForDialog,
    loadProductTypesOptionsForDialog,
    loadHAOptionsForDialog,
    loadTagsOptionsForDialog,
    loadMarketsNext: marketsAc.loadNext,
    loadMarketsPrev: marketsAc.loadPrev,
    marketsHasMoreNext: marketsAc.hasMoreNext,
    marketsLoadingMore: marketsAc.loadingMore,
    loadProductTypesOptions: productTypesAc.loadOptions,
    loadProductTypesNext: productTypesAc.loadNext,
    loadProductTypesPrev: productTypesAc.loadPrev,
    productTypesHasMoreNext: productTypesAc.hasMoreNext,
    productTypesLoadingMore: productTypesAc.loadingMore,
    loadTagsOptions: tagsAc.loadOptions,
    loadTagsNext: tagsAc.loadNext,
    loadTagsPrev: tagsAc.loadPrev,
    tagsHasMoreNext: tagsAc.hasMoreNext,
    tagsLoadingMore: tagsAc.loadingMore,
    loadHAOptions: haAc.loadOptions,
    loadHANext: haAc.loadNext,
    loadHAPrev: haAc.loadPrev,
    haHasMoreNext: haAc.hasMoreNext,
    haLoadingMore: haAc.loadingMore,
    handleMarketsInputChange,
    handleMarketsChange,
    onMarketsChange,
    handleProductTypesInputChange,
    handleHAInputChange,
    handleTagsInputChange,
    handleSearchKeyPress,
    handleSearchSubmit,
    handleSearchBlur,
    setHasFormBeenInteracted,
    deleteUserFilter,
  };
}
